import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { Icons } from "../../utils/icons";
import Sidebar from "../sidebar/clients-sidebar";
import PersonalInformation from "./details/personal-information";
import FinancialInformation from "./details/financial-information-federal";
import FinancialInformation401k from "./details/financial-information-401k";
import Comparisons from "./details/comparisons-federal";
import Results from "./details/results-federal";
import XanoAPI from "../../lib/XanoAPI";
import { useAuth } from "../../lib/AuthContext";
import Comparisons401k from "./details/comparisons-401k";
import Results401k from "./details/results-401k";
import FinancialInformation403b from "./details/financial-information-403b";
import Comparisons403b from "./details/comparions-403b";
import EngagementsList from "./details/engagements-list"; // New component for engagements
import PolicyForm from "./details/policy-form";
import ClientEditLockManager from "./ClientEditLockManager";
import UnsavedChangesModal from "../modals/unsaved-changes-modal";
import { useIntervalAutoSave } from "../../hooks/useIntervalAutoSave";
import { CopyClientButton } from "./copy-client-button";
import { ExportClientButton } from "./export-client-button";
import { SmartPasteStepModal } from "./smart-paste-step-modal";
import { ReassignClientModal } from "./reassign-client-modal";
import type { ClientType } from "../../types";

const COOKIE_NAME = "activeTab";

// ⚙️ FEATURE FLAG: Toggle unsaved changes modal
// Set to 'false' to disable unsaved changes warnings
// Set to 'true' to enable unsaved changes warnings
const ENABLE_UNSAVED_CHANGES_MODAL = true;

export default function EditForms() {
  const api = XanoAPI.getInstance();
  const auth = useAuth();
  const flags = auth?.featureFlags || [];
  const isAllowed = (flag: string) => flags.includes(flag);
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState<ClientType | null>(null);
  const [engagements, setEngagements] = useState<any[]>([]); // State to store engagements
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState<string>(
    Cookies.get(COOKIE_NAME) || "personal-information"
  );
  const [crmEnabled, setCrmEnabled] = useState<boolean>(
    !!auth?.currentUser?.module_crm_plan
  );
  // Unsaved changes modal state (controlled by ENABLE_UNSAVED_CHANGES_MODAL flag)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [formsDirty, setFormsDirty] = useState({
    personalInformation: [],
    financialInformation: [],
    policy: [],
    comparisons: [],
  });
  
  // Smart Paste modal state (lifted to parent to persist across tabs)
  const [isSmartPasteOpen, setIsSmartPasteOpen] = useState(false);
  // Store handlers per tab to prevent overwriting
  const [smartPasteHandlers, setSmartPasteHandlers] = useState<{
    personal?: {
      onPersonalDataParsed?: (data: Partial<ClientType>) => void;
      onFinancialDataParsed?: (data: Partial<ClientType>) => void;
      onPopulateSingleField?: (fieldName: string, value: any) => void;
      onSave?: () => Promise<void>;
      isSaveDisabled?: boolean;
    };
    financial?: {
      onPersonalDataParsed?: (data: Partial<ClientType>) => void;
      onFinancialDataParsed?: (data: Partial<ClientType>) => void;
      onPopulateSingleField?: (fieldName: string, value: any) => void;
      onSave?: () => Promise<void>;
      isSaveDisabled?: boolean;
    };
  }>({});
  
  const { id } = useParams();

  useEffect(() => {
    (async () => {
      try {
        await fetchClientData(id || 0);
        await fetchEngagements(id || 0);
      } catch (error) {
        console.error("Failed to fetch data", error);
      }
    })();
  }, [id, updateTrigger]);

  // Browser-level protection: Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasUnsavedChanges =
        formsDirty.personalInformation.length > 0 ||
        formsDirty.financialInformation.length > 0 ||
        formsDirty.policy.length > 0 ||
        formsDirty.comparisons.length > 0;

      if (hasUnsavedChanges && ENABLE_UNSAVED_CHANGES_MODAL) {
        // Modern browsers require returnValue to be set
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [formsDirty]);

  // Intercept browser back/forward navigation
  useEffect(() => {
    if (!ENABLE_UNSAVED_CHANGES_MODAL) return;

    const handlePopState = (e: PopStateEvent) => {
      const hasUnsavedChanges =
        formsDirty.personalInformation.length > 0 ||
        formsDirty.financialInformation.length > 0 ||
        formsDirty.policy.length > 0 ||
        formsDirty.comparisons.length > 0;

      if (hasUnsavedChanges) {
        // Prevent navigation
        e.preventDefault();
        // Push the current state back to prevent the URL from changing
        window.history.pushState(null, "", window.location.href);
        // Show the unsaved changes modal
        setIsDialogOpen(true);
        setPendingNavigation("__BACK__"); // Special marker for back navigation
      }
    };

    // Push initial state to enable interception
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [formsDirty]);

  const fetchClientData = async (id: string | number) => {
    if (!id) {
      console.error("No client ID provided");
      return;
    }

    try {
      const data = await api.getClient(id);
      setClient(data);
    } catch (error) {
      console.error("Failed to fetch client data:", error);
    }
  };

  const fetchEngagements = async (id: string | number) => {
    if (!id) {
      console.error("No client ID provided");
      return;
    }

    try {
      const data = await api.getEngagements(); // Fetch engagements for the client
      setEngagements(data.filter((c) => c.client === Number(id)));
    } catch (error) {
      console.error("Failed to fetch engagements:", error);
    }
  };

  const handleUpdate = () => setUpdateTrigger((prev) => prev + 1);

  const handleTabChange = (newTab: string) => {
    // Check if unsaved changes modal is enabled via feature flag
    if (ENABLE_UNSAVED_CHANGES_MODAL) {
      // Check for unsaved changes in forms
      const hasUnsavedChanges =
        formsDirty.personalInformation.length > 0 ||
        formsDirty.financialInformation.length > 0 ||
        formsDirty.policy.length > 0 ||
        formsDirty.comparisons.length > 0;

      if (hasUnsavedChanges) {
        console.log("⚠️ Unsaved changes detected - showing confirmation modal");
        setPendingTab(newTab);
        setIsDialogOpen(true);
        return; // Don't change tab yet - wait for user confirmation
      }
    }

    // No unsaved changes or modal disabled - proceed with tab change
    setActiveTab(newTab);
    Cookies.set(COOKIE_NAME, newTab);
    console.log(`✅ Tab changed to: ${newTab}`);
  };

  const confirmNavigation = () => {
    console.log(`✅ User confirmed navigation - discarding unsaved changes`);
    
    resetFormsDirtyState();
    
    // Check if this is a route navigation or tab change
    if (pendingNavigation === "__BACK__") {
      // Back button navigation
      closeDialog();
      window.history.back();
    } else if (pendingNavigation) {
      // Regular route navigation
      closeDialog();
      navigate(pendingNavigation);
    } else if (pendingTab) {
      // Tab change within the same page
      setActiveTab(pendingTab);
      Cookies.set(COOKIE_NAME, pendingTab);
      closeDialog();
    }
  };

  const cancelNavigation = () => {
    console.log(`❌ User cancelled navigation - staying on current tab/page`);
    closeDialog();
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setPendingTab(null);
    setPendingNavigation(null);
  };

  const resetFormsDirtyState = () =>
    setFormsDirty({
      personalInformation: [],
      financialInformation: [],
      policy: [],
      comparisons: [],
    });

  // Auto-save logic
  const hasUnsavedChanges = useCallback(() => {
    const hasPersonalChanges = formsDirty.personalInformation.length > 0;
    const hasFinancialChanges = formsDirty.financialInformation.length > 0;
    const hasPolicyChanges = formsDirty.policy.length > 0;
    const hasComparisonsChanges = formsDirty.comparisons.length > 0;

    console.log("🔍 Checking for unsaved changes:");
    console.log("  - personalInformation:", formsDirty.personalInformation);
    console.log("  - financialInformation:", formsDirty.financialInformation);
    console.log("  - policy:", formsDirty.policy);
    console.log("  - comparisons:", formsDirty.comparisons);
    console.log("  - hasPersonalChanges:", hasPersonalChanges);
    console.log("  - hasFinancialChanges:", hasFinancialChanges);
    console.log("  - hasPolicyChanges:", hasPolicyChanges);
    console.log("  - hasComparisonsChanges:", hasComparisonsChanges);

    return hasPersonalChanges || hasFinancialChanges || hasPolicyChanges || hasComparisonsChanges;
  }, [formsDirty]);

  const handleAutoSave = useCallback(async () => {
    // Find all submit buttons and filter for visible ones
    const allButtons = document.querySelectorAll('button[type="submit"]');
    let saveButton: HTMLButtonElement | null = null;

    // Find the first visible and enabled button
    for (const button of Array.from(allButtons)) {
      const btn = button as HTMLButtonElement;
      // Check if button is visible (not in a hidden parent)
      if (
        btn.offsetParent !== null && // Element is visible
        !btn.disabled // Button is not disabled
      ) {
        saveButton = btn;
        break;
      }
    }

    if (saveButton && hasUnsavedChanges()) {
      console.log("💾 AUTO-SAVE: Triggering form save via button click");
      console.log("💾 AUTO-SAVE: Button found:", saveButton.id || "no-id");
      saveButton.click();
    } else {
      if (!saveButton) {
        console.log("⏭️ AUTO-SAVE: Skipped (no visible save button found)");
      } else if (!hasUnsavedChanges()) {
        console.log("⏭️ AUTO-SAVE: Skipped (no unsaved changes)");
      }
    }
  }, [hasUnsavedChanges]);

  // Initialize auto-save hook
  const { saveStatus, lastSaveTime } = useIntervalAutoSave({
    hasUnsavedChanges,
    onSave: handleAutoSave,
    interval: 60000, // 1 minute
    enabled: true, // Auto-save is always enabled
    onSaveStart: () => console.log("💾 AUTO-SAVE: Starting..."),
    onSuccess: () => console.log("✅ AUTO-SAVE: Success"),
    onError: (error) => console.error("❌ AUTO-SAVE: Failed", error),
  });

  const renderFinancialInformation = () => {
    if (client?.type === "federal") {
      return (
        <FinancialInformation
          client={client}
          onUpdate={handleUpdate}
          handleTabChange={handleTabChange}
          formsDirty={formsDirty}
          setFormsDirty={setFormsDirty}
          fetchClientData={fetchClientData}
          resetFormsDirtyState={resetFormsDirtyState}
          saveStatus={saveStatus}
          lastSaveTime={lastSaveTime}
          isSmartPasteOpen={isSmartPasteOpen}
          setIsSmartPasteOpen={setIsSmartPasteOpen}
          setSmartPasteHandlers={setSmartPasteHandlers}
        />
      );
    } else if (client?.type === "401k") {
      return (
        <FinancialInformation401k
          client={client}
          onUpdate={handleUpdate}
          handleTabChange={handleTabChange}
          formsDirty={formsDirty}
          setFormsDirty={setFormsDirty}
          fetchClientData={fetchClientData}
          resetFormsDirtyState={resetFormsDirtyState}
          saveStatus={saveStatus}
          lastSaveTime={lastSaveTime}
          isSmartPasteOpen={isSmartPasteOpen}
          setIsSmartPasteOpen={setIsSmartPasteOpen}
          setSmartPasteHandlers={setSmartPasteHandlers}
        />
      );
    } else if (client?.type === "403b") {
      return (
        <FinancialInformation403b
          client={client}
          onUpdate={handleUpdate}
          handleTabChange={handleTabChange}
          formsDirty={formsDirty}
          setFormsDirty={setFormsDirty}
          fetchClientData={fetchClientData}
          resetFormsDirtyState={resetFormsDirtyState}
          saveStatus={saveStatus}
          lastSaveTime={lastSaveTime}
          isSmartPasteOpen={isSmartPasteOpen}
          setIsSmartPasteOpen={setIsSmartPasteOpen}
          setSmartPasteHandlers={setSmartPasteHandlers}
        />
      );
    }
    return null;
  };

  return (
    <div className="flex-1 space-y-4 p-8 pt-6 container">
      {/* Client Lock Manager - Shows lock status and manages lock lifecycle */}
      <ClientEditLockManager />

      <div className="pt-10 mt-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icons.userFramed className="w-12 h-12 text-accent" />
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-accent">
                Edit Client
              </h2>
              <p className="text-muted-foreground">
                This is where you can edit a client&apos;s details
              </p>
            </div>
          </div>
          
          {/* Action buttons: Copy, Export, and Reassign (admin only) */}
          {client && (
            <div className="flex gap-2">
              <CopyClientButton 
                client={client} 
                variant="outline"
                onSuccess={(newClientId) => {
                  navigate(`/edit-client/${newClientId}`);
                }}
              />
              <ExportClientButton 
                client={client}
              />
              {auth?.currentUser?.user_permissions?.[0]?.role === "admin" && (
                <ReassignClientModal
                  client={client}
                  onSuccess={() => {
                    // Refetch client data after reassignment
                    if (client?.id) {
                      fetchClientData(client.id);
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
        <Sidebar activeTab={activeTab} />
      </div>

      <div className="pt-10">
        <ul className="flex font-sans bg-gray-100 rounded">
          <li
            className={`personal-information text-gray w-full text-left text-base py-3 px-4 cursor-pointer flex rounded ${
              activeTab === "personal-information" ? "bg-accent text-white" : ""
            }`}
            onClick={() => handleTabChange("personal-information")}
          >
            <Icons.personal className="w-6 h-6 mr-2" />
            Personal Information
          </li>
          <li
            className={`financial-information text-gray w-full text-left text-base py-3 px-4 cursor-pointer flex rounded ${
              activeTab === "financial-information"
                ? "bg-accent text-white"
                : ""
            }`}
            onClick={() => handleTabChange("financial-information")}
          >
            <Icons.financial className="w-6 h-6 mr-2" />
            Financial Information
          </li>
          <li
            className={`comparisons text-gray w-full text-left text-base py-3 px-4 cursor-pointer flex rounded ${
              activeTab === "comparisons" ? "bg-accent text-white" : ""
            }`}
            onClick={() => handleTabChange("comparisons")}
          >
            <Icons.newspaper className="w-6 h-6 mr-2" />
            Comparisons
          </li>
          <li
            className={`results text-gray w-full text-left text-base py-3 px-4 cursor-pointer flex rounded ${
              activeTab === "results" ? "bg-accent text-white" : ""
            }`}
            onClick={() => handleTabChange("results")}
          >
            <Icons.results className="w-6 h-6 mr-2" />
            Results
          </li>
          {crmEnabled && (
            <li
              className={`policy text-gray w-full text-left text-base py-3 px-4 cursor-pointer flex rounded ${
                activeTab === "policy" ? "bg-accent text-white" : ""
              }`}
              onClick={() => handleTabChange("policy")}
            >
              <Icons.policy className="w-6 h-6 mr-2" />
              Policy
            </li>
          )}
          {isAllowed("calendar") && crmEnabled && (
            <li
              className={`engagements text-gray w-full text-left text-base py-3 px-4 cursor-pointer flex rounded ${
                activeTab === "engagements" ? "bg-accent text-white" : ""
              }`}
              onClick={() => handleTabChange("engagements")}
            >
              <Icons.calendar className="w-6 h-6 mr-2" />
              Engagements
            </li>
          )}
        </ul>

        <div
          className={
            activeTab === "personal-information" ? "visible" : "hidden"
          }
        >
          {client && (
            <PersonalInformation
              client={client}
              onUpdate={handleUpdate}
              handleTabChange={handleTabChange}
              formsDirty={formsDirty}
              setFormsDirty={setFormsDirty}
              fetchClientData={fetchClientData}
              resetFormsDirtyState={resetFormsDirtyState}
              saveStatus={saveStatus}
              lastSaveTime={lastSaveTime}
              isSmartPasteOpen={isSmartPasteOpen}
              setIsSmartPasteOpen={setIsSmartPasteOpen}
              setSmartPasteHandlers={setSmartPasteHandlers}
            />
          )}
        </div>

        <div
          className={
            activeTab === "financial-information" ? "visible" : "hidden"
          }
        >
          {client && renderFinancialInformation()}
        </div>

        <div className={activeTab === "comparisons" ? "visible" : "hidden"}>
          {client && client?.type === "federal" && (
            <Comparisons
              client={client}
              formsDirty={formsDirty}
              setFormsDirty={setFormsDirty}
              fetchClientData={fetchClientData}
              resetFormsDirtyState={resetFormsDirtyState}
              saveStatus={saveStatus}
              lastSaveTime={lastSaveTime}
            />
          )}

          {client && client?.type === "401k" && (
            <Comparisons401k
              client={client}
              formsDirty={formsDirty}
              setFormsDirty={setFormsDirty}
              fetchClientData={fetchClientData}
              resetFormsDirtyState={resetFormsDirtyState}
              saveStatus={saveStatus}
              lastSaveTime={lastSaveTime}
            />
          )}

          {client && client?.type === "403b" && (
            <Comparisons403b
              client={client}
              formsDirty={formsDirty}
              setFormsDirty={setFormsDirty}
              fetchClientData={fetchClientData}
              resetFormsDirtyState={resetFormsDirtyState}
              saveStatus={saveStatus}
              lastSaveTime={lastSaveTime}
            />
          )}
        </div>

        <div className={activeTab === "results" ? "visible" : "hidden"}>
          {client && client?.type === "federal" && <Results client={client} />}
          {client && client?.type === "401k" && <Results401k client={client} />}
        </div>

        <div className={activeTab === "engagements" ? "visible" : "hidden"}>
          {engagements && (
            <EngagementsList
              engagements={engagements}
              clientId={Number(id)}
              onAddEngagement={() => {}}
              // onDeleteEngagement={() => {}}
            />
          )}
        </div>

        <div className={activeTab === "policy" ? "visible" : "hidden"}>
          {client && (
            <PolicyForm
              client={client}
              formsDirty={formsDirty}
              setFormsDirty={setFormsDirty}
              fetchClientData={fetchClientData}
              resetFormsDirtyState={resetFormsDirtyState}
              saveStatus={saveStatus}
              lastSaveTime={lastSaveTime}
            />
          )}
        </div>
      </div>

      {/* Unsaved Changes Confirmation Modal (only shown if ENABLE_UNSAVED_CHANGES_MODAL is true) */}
      {ENABLE_UNSAVED_CHANGES_MODAL && (
        <UnsavedChangesModal
          isOpen={isDialogOpen}
          onCancel={cancelNavigation}
          onConfirm={confirmNavigation}
        />
      )}
      
      {/* Smart Paste Modal (persists across all tabs) */}
      {client && (
        <SmartPasteStepModal
          isOpen={isSmartPasteOpen}
          onClose={() => setIsSmartPasteOpen(false)}
          // Use handlers based on active tab
          onPersonalDataParsed={
            activeTab === "personal-information" && smartPasteHandlers.personal?.onPersonalDataParsed
              ? smartPasteHandlers.personal.onPersonalDataParsed
              : activeTab === "financial-information" && smartPasteHandlers.financial?.onPersonalDataParsed
              ? smartPasteHandlers.financial.onPersonalDataParsed
              : (() => {
                  console.error("❌ [Smart Paste] onPersonalDataParsed handler not registered for active tab:", activeTab);
                })
          }
          onFinancialDataParsed={
            activeTab === "personal-information" && smartPasteHandlers.personal?.onFinancialDataParsed
              ? smartPasteHandlers.personal.onFinancialDataParsed
              : activeTab === "financial-information" && smartPasteHandlers.financial?.onFinancialDataParsed
              ? smartPasteHandlers.financial.onFinancialDataParsed
              : (() => {
                  console.error("❌ [Smart Paste] onFinancialDataParsed handler not registered for active tab:", activeTab);
                })
          }
          onPopulateSingleField={
            activeTab === "personal-information" && smartPasteHandlers.personal?.onPopulateSingleField
              ? smartPasteHandlers.personal.onPopulateSingleField
              : activeTab === "financial-information" && smartPasteHandlers.financial?.onPopulateSingleField
              ? smartPasteHandlers.financial.onPopulateSingleField
              : (() => {
                  console.error("❌ [Smart Paste] onPopulateSingleField handler not registered for active tab:", activeTab);
                })
          }
          onSave={
            activeTab === "personal-information" && smartPasteHandlers.personal?.onSave
              ? smartPasteHandlers.personal.onSave
              : activeTab === "financial-information" && smartPasteHandlers.financial?.onSave
              ? smartPasteHandlers.financial.onSave
              : (async () => {
                  console.error("❌ [Smart Paste] onSave handler not registered for active tab:", activeTab);
                })
          }
          isSaveDisabled={
            activeTab === "personal-information" && smartPasteHandlers.personal?.isSaveDisabled !== undefined
              ? smartPasteHandlers.personal.isSaveDisabled
              : activeTab === "financial-information" && smartPasteHandlers.financial?.isSaveDisabled !== undefined
              ? smartPasteHandlers.financial.isSaveDisabled
              : true
          }
          currentClient={client}
          handleTabChange={handleTabChange}
        />
      )}
    </div>
  );
}
